/**
 * warRoomMappers.ts - Beta 3 War Room V6
 * 
 * Transforms API data into display-ready models for War Room cards.
 * MUST use SSOT roster helpers from rosterHelpers.ts.
 * 
 * @see WAR_ROOM_DEV_PLAN.md Section 3.5
 * @see WAR_ROOM_V6_DEV_PLAN_APPROVED.md
 * 
 * HERO LOGIC (CORRECTED):
 * - Hero = incomplete AND < 60 minutes to lock (the "scramble" moment)
 * - If timeToLockMs <= 0 → LIVE bucket (not Hero)
 * - If 0 < timeToLockMs <= 60m → HERO candidate (earliest lock wins)
 * - If timeToLockMs > 60m → non-hero incomplete list (normal "Fill Gaps")
 * 
 * V6 CHANGES:
 * - Extended WarRoomCampaignCard with metrics fields
 * - Added membership parameter to mapper functions
 * - Added entry unwrap compatibility for {entry, state} API wrapper
 * - Metrics formulas copied verbatim from PoolDetails.tsx (SSOT)
 */

import { 
  countCompletedSlots, 
  calculateTotalUnits, 
  isRosterComplete,
  RosterSlot 
} from './rosterHelpers';

// =============================================================================
// Types
// =============================================================================

export interface Pool {
  id: number;
  pool_id: string;
  pool_name: string;
  domain: string;
  mode: 'gm' | 'pick';
  status: 'draft' | 'active' | 'locked' | 'completed' | 'cancelled' | 'pending';
  start_date?: string;
  end_date?: string;
  lock_time?: string;
  initial_bankroll: number;
  participant_count?: number;
  current_participants?: number;
  // GM Config (from pool or defaults)
  gm_slot_count?: number;
  gm_total_units?: number;
}

export interface EntryData {
  roster: RosterSlot[];
  status?: string;
  isLocked?: boolean;
  score?: number;
  rank?: number;
}

/**
 * PoolMembership - matches schema from NORTHSTAR_COMPLETE_SCHEMA_REFERENCE_v2.md
 * Used for efficiency/effectiveness calculations
 */
export interface PoolMembership {
  id: number;
  pool_id: string;
  user_id: string;
  current_balance: number;
  starting_balance?: number;
  initial_balance?: number;
  predictions_made: number;
  correct_predictions?: number;
  predictions_won?: number;
  incorrect_predictions?: number;
  pending_predictions?: number;
  total_staked?: number;
  total_won?: number;
  total_lost?: number;
  roi?: number;
  is_active?: boolean;
  is_depleted?: boolean;
}

/**
 * WarRoomCampaignCard - V6 Extended
 * 
 * Added fields for V6:
 * - efficiencyPct: wins / total props (Game Plan Efficiency)
 * - effectivenessPct: dollars won / dollars at risk (Game Plan Effectiveness)
 * - Raw components for optional future use
 */
export interface WarRoomCampaignCard {
  poolId: string;
  name: string;
  league: string;
  lockTime: Date;
  entrantCount: number;
  slotsTotal: number;
  slotsFilled: number;
  impactUsed: number;
  impactTotal: number;
  isComplete: boolean;
  isLocked: boolean;
  isLive: boolean;
  isFinal: boolean;
  statusLabel: string;
  timeToLockMs: number;
  // For live pools
  currentScore?: number;
  currentRank?: number;
  // V6 NEW: Metrics fields
  efficiencyPct?: number;
  effectivenessPct?: number;
  wins?: number;
  totalPredictions?: number;
  totalWon?: number;
  totalLost?: number;
}

// =============================================================================
// Constants (derived from pool config or shared defaults)
// =============================================================================

/** 60 minutes in milliseconds - threshold for Hero eligibility */
const SIXTY_MINUTES_MS = 60 * 60 * 1000;

/**
 * Get slots/units from pool config or use defaults.
 * These should match what Tunnel uses.
 */
const getPoolConstants = (pool: Pool) => ({
  slotsTotal: pool.gm_slot_count ?? 7,
  unitsTotal: pool.gm_total_units ?? 100,
});

// =============================================================================
// Mappers
// =============================================================================

/**
 * Map domain to display league name.
 */
const mapDomainToLeague = (domain: string): string => {
  const leagueMap: Record<string, string> = {
    nfl: 'NFL',
    nba: 'NBA',
    ncaab: 'NCAAB',
    ncaaf: 'NCAAF',
    mlb: 'MLB',
    nhl: 'NHL',
  };
  return leagueMap[domain.toLowerCase()] || domain.toUpperCase();
};

/**
 * Derive status label from pool state.
 * NO ENUMS - just display strings.
 * 
 * Labels:
 * - "Auto-Lock Armed" = Pre-lock, roster complete
 * - "Live — Monitor" = Post-lock, in progress
 * - "Final — Review" = Completed/settled
 */
const deriveStatusLabel = (
  poolStatus: string,
  isLocked: boolean,
  isComplete: boolean,
  isFinal: boolean
): string => {
  if (isFinal || poolStatus === 'completed') {
    return 'Final — Review';
  }
  if (isLocked || poolStatus === 'locked') {
    return 'Live — Monitor';
  }
  if (isComplete) {
    return 'Auto-Lock Armed';
  }
  // Incomplete, pre-lock - no special label (shown via critical styling)
  return '';
};

/**
 * Check if pool is final based on roster resolution.
 * V9 FIX: SSOT alignment with Field Report / H2H logic.
 * 
 * A pool is FINAL when ALL filled roster slots have a resolution status
 * of HIT, MISS, or PUSH. This matches Field Report's classification logic.
 * 
 * @param roster - Array of roster slots from entry
 * @returns true if all slots are resolved (final)
 */
const isFinalByRoster = (roster: any[]): boolean => {
  if (!roster || roster.length === 0) return false;

  const isResolved = (slot: any): boolean => {
    // Check all possible resolution status field locations
    const status =
      slot?.resolution?.status ??
      slot?.resolution_status ??
      slot?.resolutionStatus ??
      slot?.resolution?.resolutionStatus ??
      null;

    return !!status && ['HIT', 'MISS', 'PUSH'].includes(String(status).toUpperCase());
  };

  // Treat as final only if every roster slot is resolved
  return roster.every(isResolved);
};

/**
 * Check if pool is currently live (locked but not completed).
 * V6 FIX: Also accept poolEventStatus and entryState from API wrapper for SSOT.
 * V9 FIX: This function is now only called AFTER isFinal check in mapToWarRoomCard.
 */
const isPoolLive = (pool: Pool, poolEventStatus?: string, entryState?: string): boolean => {
  // SSOT: If API tells us it's live, trust it
  if (poolEventStatus === 'live') return true;
  
  // Entry state LOCKED means games have started
  if (entryState === 'LOCKED') return true;
  
  if (pool.status === 'locked') return true;
  
  // Also check if lock_time has passed
  if (pool.lock_time) {
    const lockTime = new Date(pool.lock_time);
    return lockTime <= new Date() && pool.status !== 'completed';
  }
  
  return false;
};

/**
 * Unwrap entry from API response.
 * 
 * V6 FIX: Backend returns { entry: <payload|null>, state: "..." }
 * This function handles both:
 * - Raw EntryData (legacy/direct)
 * - Wrapped { entry, state } (current API)
 * 
 * @param entry - Raw API response or null
 * @returns Unwrapped entry data or null
 */
const unwrapEntry = (entry: EntryData | { entry: EntryData | null; state?: string } | null): EntryData | null => {
  if (!entry) return null;
  
  // Check if wrapped: has 'entry' property at top level
  if ('entry' in entry && entry.entry !== undefined) {
    return entry.entry;
  }
  
  // Already unwrapped or raw EntryData
  return entry as EntryData;
};

/**
 * Calculate metrics from membership data.
 * SSOT: Formulas copied exactly from PoolDetails.tsx
 * 
 * @param membership - Pool membership or undefined
 * @returns Calculated metrics
 */
const calculateMetrics = (membership?: PoolMembership, roster?: any[]): {
  efficiencyPct: number;
  effectivenessPct: number;
  wins: number;
  totalPredictions: number;
  totalWon: number;
  totalLost: number;
} => {
  // FIX #21: Calculate wins/total from roster allocations (SSOT)
  // This ensures hit counter matches actual resolved allocations
  let wins = 0;
  let totalPredictions = 0;
  
  if (roster && roster.length > 0) {
    roster.forEach(slot => {
      const status = 
        slot?.resolution?.status ??
        slot?.resolution_status ??
        slot?.resolutionStatus ??
        null;
      
      if (status && ['HIT', 'MISS', 'PUSH'].includes(String(status).toUpperCase())) {
        totalPredictions++;
        if (String(status).toUpperCase() === 'HIT') {
          wins++;
        }
      }
    });
  }
  
  // Fallback to membership if roster didn't provide resolved data
  if (totalPredictions === 0) {
    wins = membership?.correct_predictions ?? membership?.predictions_won ?? 0;
    totalPredictions = membership?.predictions_made ?? 0;
  }
  
  const efficiencyPct = totalPredictions > 0 ? (wins / totalPredictions) * 100 : 0;

  // Game Plan Effectiveness: Dollars Won / Dollars at Risk
  const totalWon = membership?.total_won ?? 0;
  const totalLost = membership?.total_lost ?? 0;
  const totalRisked = totalWon + totalLost;
  const effectivenessPct = totalRisked > 0 ? (totalWon / totalRisked) * 100 : 0;

  return {
    efficiencyPct,
    effectivenessPct,
    wins,
    totalPredictions,
    totalWon,
    totalLost,
  };
};

/**
 * Map a single pool + entry + membership to a WarRoomCampaignCard.
 * 
 * V6 CHANGES:
 * - Added membership parameter for metrics
 * - Added entry unwrap for API compatibility
 * - Computes efficiency/effectiveness metrics
 */
export const mapToWarRoomCard = (
  pool: Pool,
  entry: EntryData | { entry: EntryData | null; state?: string; poolEventStatus?: string } | null,
  membership?: PoolMembership
): WarRoomCampaignCard => {
  const { slotsTotal, unitsTotal } = getPoolConstants(pool);
  
  // V6 FIX: Extract poolEventStatus and state from wrapper before unwrapping
  const poolEventStatus = (entry && 'poolEventStatus' in entry) ? entry.poolEventStatus : undefined;
  const entryState = (entry && 'state' in entry) ? entry.state : undefined;
  
  // V6 FIX: Unwrap entry (handles both raw and {entry, state} shapes)
  const rawEntry = unwrapEntry(entry);
  
  // Extract roster or use empty array
  const roster = rawEntry?.roster || [];
  
  // Use SSOT helpers
  const slotsFilled = countCompletedSlots(roster);
  const impactUsed = calculateTotalUnits(roster);
  const isComplete = isRosterComplete(roster, slotsTotal, unitsTotal);
  
  // V9 FIX: Determine FINAL using SSOT sources + roster inference
  // This aligns War Room with Field Report / H2H logic where FINAL = all slots resolved
  const inferredFinal = isFinalByRoster(roster);
  const isFinal =
    inferredFinal ||
    poolEventStatus === 'final' ||
    poolEventStatus === 'completed' ||
    entryState === 'FINAL' ||
    entryState === 'COMPLETED' ||
    pool.status === 'completed';

  // V9 FIX: Determine LIVE only if NOT final (prevents finished pools showing as LIVE)
  const isLive = !isFinal && isPoolLive(pool, poolEventStatus, entryState);
  
  // Handle both camelCase (isLocked) and snake_case (is_locked) from API
  // Check multiple possible lock indicators from API
  const isLocked = 
    rawEntry?.isLocked || 
    (rawEntry as any)?.is_locked || 
    (rawEntry as any)?.status === 'locked' ||
    (rawEntry as any)?.lockedAt != null ||
    isLive;
  
  // Get lock time (use end_date as fallback)
  const lockTimeStr = pool.lock_time || pool.end_date || new Date().toISOString();
  const lockTime = new Date(lockTimeStr);
  const timeToLockMs = lockTime.getTime() - Date.now();
  
  // Derive status label
  const statusLabel = deriveStatusLabel(pool.status, isLocked, isComplete, isFinal);
  
  // Get entrant count
  const entrantCount = pool.participant_count ?? pool.current_participants ?? 0;
  
  // V6 NEW: Calculate metrics from membership AND roster (FIX #21)
  const metrics = calculateMetrics(membership, roster);
  
  return {
    poolId: pool.pool_id,
    name: pool.pool_name,
    league: mapDomainToLeague(pool.domain),
    lockTime,
    entrantCount,
    slotsTotal,
    slotsFilled,
    impactUsed,
    impactTotal: unitsTotal,
    isComplete,
    isLocked,
    isLive,
    isFinal,
    statusLabel,
    timeToLockMs,
    currentScore: rawEntry?.score,
    currentRank: rawEntry?.rank,
    // V6 NEW: Metrics
    ...metrics,
  };
};

/**
 * Map multiple pools + entries + memberships to WarRoomCampaignCards.
 * Filters to GM mode only.
 * 
 * V6 CHANGES:
 * - Added membershipByPoolId parameter
 * - Passes membership to mapToWarRoomCard for metrics
 */
export const mapToWarRoomCards = (
  pools: Pool[],
  entries: Map<string, EntryData | null>,
  membershipByPoolId?: Map<string, PoolMembership>
): WarRoomCampaignCard[] => {
  return pools
    .filter(pool => pool.mode === 'gm') // GM only for Beta 3
    .map(pool => {
      const entry = entries.get(pool.pool_id) || null;
      const membership = membershipByPoolId?.get(pool.pool_id);
      return mapToWarRoomCard(pool, entry, membership);
    });
};

// =============================================================================
// Triage Helpers
// =============================================================================

/**
 * Sort campaigns by lock time (ascending - earliest first).
 */
export const sortByLockTime = (
  cards: WarRoomCampaignCard[]
): WarRoomCampaignCard[] => {
  return [...cards].sort((a, b) => a.lockTime.getTime() - b.lockTime.getTime());
};

/**
 * Get incomplete campaigns that have actual empty slots (true gaps).
 * EXCLUDES "slots full, cap available" state — those get UPDATE ROSTER treatment.
 */
export const getIncompleteCampaigns = (
  cards: WarRoomCampaignCard[]
): WarRoomCampaignCard[] => {
  return cards.filter(c => !c.isComplete && !c.isLive && !c.isFinal && c.slotsFilled < c.slotsTotal);
};

/**
 * Get campaigns where all slots are filled but cap not fully deployed.
 * CTA: UPDATE ROSTER | Badge: "X units available"
 */
export const getSlotsFullCapAvailable = (
  cards: WarRoomCampaignCard[]
): WarRoomCampaignCard[] => {
  return cards.filter(c => !c.isComplete && !c.isLive && !c.isFinal && c.slotsFilled >= c.slotsTotal);
};

/**
 * Get live campaigns (locked, in progress).
 */
export const getLiveCampaigns = (
  cards: WarRoomCampaignCard[]
): WarRoomCampaignCard[] => {
  return cards.filter(c => c.isLive && !c.isFinal);
};

/**
 * Get complete but not live campaigns (ready, pre-lock).
 */
export const getCompleteCampaigns = (
  cards: WarRoomCampaignCard[]
): WarRoomCampaignCard[] => {
  return cards.filter(c => c.isComplete && !c.isLive && !c.isFinal);
};

/**
 * Get final campaigns (completed/settled).
 */
export const getFinalCampaigns = (
  cards: WarRoomCampaignCard[]
): WarRoomCampaignCard[] => {
  return cards.filter(c => c.isFinal);
};

/**
 * Determine Hero candidate per architect rules (CORRECTED).
 * 
 * Hero = incomplete AND < 60 minutes to lock (the "scramble" moment)
 * - If timeToLockMs <= 0 → LIVE bucket (not Hero)
 * - If 0 < timeToLockMs <= 60m → HERO candidate (earliest lock wins)
 * - If timeToLockMs > 60m → non-hero incomplete list
 * 
 * Returns null if no valid Hero candidate.
 */
export const getHeroCandidate = (
  cards: WarRoomCampaignCard[]
): WarRoomCampaignCard | null => {
  const incomplete = getIncompleteCampaigns(cards);
  const sorted = sortByLockTime(incomplete);
  
  // CORRECTED: Hero is UNDER 60 minutes (the scramble moment)
  // Must be > 0 (not already live) AND <= 60 minutes
  const heroCandidate = sorted.find(c => 
    c.timeToLockMs > 0 && c.timeToLockMs <= SIXTY_MINUTES_MS
  );
  
  return heroCandidate || null;
};

/**
 * Get Critical List items (incomplete pools NOT in Hero).
 * 
 * CORRECTED: Now includes pools > 60 min (since Hero is < 60 min)
 * Sorted by lock time ascending.
 */
export const getCriticalListCampaigns = (
  cards: WarRoomCampaignCard[],
  heroPoolId: string | null
): WarRoomCampaignCard[] => {
  const incomplete = getIncompleteCampaigns(cards);
  const filtered = heroPoolId 
    ? incomplete.filter(c => c.poolId !== heroPoolId)
    : incomplete;
  
  return sortByLockTime(filtered);
};

// =============================================================================
// Time Formatting Utilities
// =============================================================================

/**
 * Format time to lock for display.
 */
export const formatTimeToLock = (timeToLockMs: number): { 
  text: string; 
  isUrgent: boolean;
  isLive: boolean;
} => {
  if (timeToLockMs <= 0) {
    return { text: 'In Progress', isUrgent: false, isLive: true };
  }
  
  const minutes = Math.floor(timeToLockMs / (1000 * 60));
  const hours = Math.floor(timeToLockMs / (1000 * 60 * 60));
  const days = Math.floor(timeToLockMs / (1000 * 60 * 60 * 24));
  
  if (minutes < 60) {
    return { text: `${minutes}m`, isUrgent: true, isLive: false };
  }
  if (hours < 24) {
    return { text: `${hours}h`, isUrgent: false, isLive: false };
  }
  return { text: `${days}d`, isUrgent: false, isLive: false };
};

/**
 * Format countdown timer (HH:MM:SS).
 */
export const formatCountdown = (timeToLockMs: number): string => {
  if (timeToLockMs <= 0) return '00:00:00';
  
  const hours = Math.floor(timeToLockMs / (1000 * 60 * 60));
  const minutes = Math.floor((timeToLockMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeToLockMs % (1000 * 60)) / 1000);
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Format lock time as readable date/time.
 */
export const formatLockTime = (lockTime: Date): string => {
  const options: Intl.DateTimeFormatOptions = { 
    weekday: 'short', 
    hour: 'numeric', 
    minute: '2-digit' 
  };
  return lockTime.toLocaleString('en-US', options);
};
